// Ícones: os da interface (traço fino, Lucide, licença ISC) e os ícones das
// páginas. O ícone de uma página pode ser:
//   - um emoji ("📄");
//   - um ícone colorido ("lucide:rocket:blue");
//   - uma imagem (URL de arquivo enviado ou link).

import {
  FileText, NotebookPen, BookOpen, Bookmark, Briefcase, Building, Calendar, CalendarCheck, CalendarDays, ChartColumn, ChartLine,
  SquareCheck, CircleCheck, ClipboardList, Clock, Code, Settings, Cpu, Database, Flag, FlaskConical, Folder, FolderKanban, Gift,
  Globe, GraduationCap, Hammer, Heart, House, Inbox, KeyRound, SquareKanban, Layers, LayoutDashboard, Lightbulb, Link, ListChecks,
  ListTodo, Lock, Mail, Map as MapIcon, Megaphone, MessageCircle, Mic, Monitor, Package, Palette, Paperclip, Pencil, Phone, Pin, Play,
  Puzzle, Rocket, Search, Send, Server, Shield, ShoppingCart, Smartphone, Sparkles, Star, Stethoscope, Tag, Target, Terminal,
  TestTube, ThumbsUp, Timer, TrendingUp, Trophy, Truck, User, Users, Video, Wallet, Wrench, Zap, Bug, Bell, Box, Camera, Coffee,
  Crown, DollarSign, FileCheck, GitBranch, Hospital, Landmark, Headset, Handshake, Receipt, Activity, Brain, Bot, NotebookTabs,
  Ticket, Kanban,
  Plus, Download, Trash, ShieldCheck, ChevronsLeft, Ellipsis, StarOff, Copy, RotateCcwClock, SendHorizontal, AtSign, WandSparkles,
  MessageSquare, Check, X, Upload, Image, ArrowUpRight, Move, BellRing, Archive, LogOut, UserPlus, UserCog, ChartPie, FileDown,
  Maximize2, Type, LockOpen, ChevronDown, ZoomIn, ZoomOut, ClipboardPaste, AudioLines, MicOff, Hand, Network,
} from "lucide";

// Ícones que dá para escolher para as páginas (nome -> desenho).
export const PAGE_ICONS = {
  "file-text": FileText, "notebook-pen": NotebookPen, "book-open": BookOpen, bookmark: Bookmark, briefcase: Briefcase, building: Building,
  calendar: Calendar, "calendar-check": CalendarCheck, "calendar-days": CalendarDays, "chart-column": ChartColumn, "chart-line": ChartLine,
  "square-check": SquareCheck, "circle-check": CircleCheck, "clipboard-list": ClipboardList, clock: Clock, code: Code, settings: Settings,
  cpu: Cpu, database: Database, flag: Flag, flask: FlaskConical, folder: Folder, "folder-kanban": FolderKanban, gift: Gift, globe: Globe,
  "graduation-cap": GraduationCap, hammer: Hammer, heart: Heart, house: House, inbox: Inbox, key: KeyRound, kanban: SquareKanban,
  "kanban-cols": Kanban, layers: Layers, dashboard: LayoutDashboard, lightbulb: Lightbulb, link: Link, "list-checks": ListChecks,
  "list-todo": ListTodo, lock: Lock, mail: Mail, map: MapIcon, megaphone: Megaphone, message: MessageCircle, mic: Mic, monitor: Monitor,
  package: Package, palette: Palette, paperclip: Paperclip, pencil: Pencil, phone: Phone, pin: Pin, play: Play, puzzle: Puzzle,
  rocket: Rocket, search: Search, send: Send, server: Server, shield: Shield, cart: ShoppingCart, smartphone: Smartphone,
  sparkles: Sparkles, star: Star, stethoscope: Stethoscope, tag: Tag, target: Target, terminal: Terminal, "test-tube": TestTube,
  "thumbs-up": ThumbsUp, timer: Timer, "trending-up": TrendingUp, trophy: Trophy, truck: Truck, user: User, users: Users, video: Video,
  wallet: Wallet, wrench: Wrench, zap: Zap, bug: Bug, bell: Bell, box: Box, camera: Camera, coffee: Coffee, crown: Crown,
  dollar: DollarSign, "file-check": FileCheck, "git-branch": GitBranch, hospital: Hospital, landmark: Landmark, headset: Headset,
  handshake: Handshake, receipt: Receipt, activity: Activity, brain: Brain, bot: Bot, "notebook-tabs": NotebookTabs, ticket: Ticket,
};

// Ícones da própria interface.
const UI = {
  search: Search, home: House, inbox: Inbox, tasks: ListTodo, ai: Sparkles, plus: Plus, import: Download, trash: Trash,
  settings: Settings, admin: ShieldCheck, collapse: ChevronsLeft, more: Ellipsis, star: Star, unstar: StarOff, copy: Copy,
  history: RotateCcwClock, send: SendHorizontal, mention: AtSign, wand: WandSparkles, comment: MessageSquare, check: Check, x: X,
  upload: Upload, image: Image, open: ArrowUpRight, move: Move, bell: BellRing, archive: Archive, logout: LogOut, userPlus: UserPlus,
  userCog: UserCog, chart: ChartPie, export: FileDown, wide: Maximize2, type: Type, lock: Lock, unlock: LockOpen, users: Users,
  space: Layers, page: FileText, database: Database, link: Link, clock: Clock, calendar: Calendar,
  chevronDown: ChevronDown, zoomIn: ZoomIn, zoomOut: ZoomOut, paste: ClipboardPaste, camera: Camera, user: User,
  voice: AudioLines, mic: Mic, micOff: MicOff, hand: Hand, org: Network, mail: Mail, phone: Phone, globe: Globe,
};

// Cores dos ícones (iguais às cores das etiquetas).
export const ICON_COLORS = {
  default: "#55534e", gray: "#8c8b87", brown: "#9f6b53", orange: "#d9730d", yellow: "#cb912f",
  green: "#448361", blue: "#337ea9", purple: "#9065b0", pink: "#c14c8a", red: "#d44c47",
};

const SVG = "http://www.w3.org/2000/svg";

export function svg(node, { size = 18, color = "currentColor", stroke = 1.75 } = {}) {
  const s = document.createElementNS(SVG, "svg");
  for (const [k, v] of Object.entries({ xmlns: SVG, width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: color,
    "stroke-width": stroke, "stroke-linecap": "round", "stroke-linejoin": "round", "aria-hidden": "true" })) s.setAttribute(k, v);
  for (const [tag, attrs] of node) {
    const c = document.createElementNS(SVG, tag);
    for (const [k, v] of Object.entries(attrs)) c.setAttribute(k, v);
    s.append(c);
  }
  s.classList.add("svg-icon");
  return s;
}

// Ícone da interface.
export const ui = (name, size = 16) => svg(UI[name] || UI.page, { size });

export const isImageIcon = (v) => typeof v === "string" && /^(https?:|data:image\/)/i.test(v);
export const isLucideIcon = (v) => typeof v === "string" && v.startsWith("lucide:");

// Elemento que mostra o ícone de uma página (emoji, ícone colorido ou imagem).
export function iconNode(value, fallback = "📄") {
  const v = value || fallback;
  if (isLucideIcon(v)) {
    const [, name, color] = v.split(":");
    const span = document.createElement("span");
    span.className = "pg-icon pg-icon-svg";
    span.append(svg(PAGE_ICONS[name] || FileText, { size: 18, color: ICON_COLORS[color] || ICON_COLORS.default, stroke: 2 }));
    return span;
  }
  if (isImageIcon(v)) {
    const img = document.createElement("img");
    img.className = "pg-icon pg-icon-img";
    img.src = v;
    img.alt = "";
    img.draggable = false;
    return img;
  }
  const span = document.createElement("span");
  span.className = "pg-icon";
  span.textContent = v;
  return span;
}

// Só o texto (para <option> e textos): emoji, ou nada se for ícone/imagem.
export const iconText = (value, fallback = "") => (value && !isLucideIcon(value) && !isImageIcon(value) ? value : fallback);
